/**
 * React binding for the user-preferences store.
 *
 * Subscribes to the storage key so changes from the profile page (or
 * any other screen) propagate without prop-drilling. Defaults are
 * taken from `DEFAULT_USER_PREFERENCES` and merged with whatever was
 * persisted under the active user's id.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useContext } from 'react';
import { AuthContext } from '../auth/AuthContext';
import {
  DEFAULT_USER_PREFERENCES,
  loadUserPreferences,
  saveBatteryPreferences,
  saveUserPreferences,
  type UserPreferences,
} from './user-preferences';

const STORAGE_EVENT = 'nx:user-prefs-change';

export function useUserPreferences() {
  // The auth CONTEXT rather than `useAuth()`, which THROWS when no
  // provider is mounted -- which made the `?? 'anonymous'` fallback on
  // the next line unreachable, and made every preference-reading
  // component impossible to render on its own. Preferences are a
  // per-person convenience, not an authorisation decision; with no
  // signed-in user they belong to 'anonymous', which is what this line
  // always said it did.
  const auth = useContext(AuthContext);
  const userId = auth?.user?.id ?? 'anonymous';
  const [prefs, setPrefs] = useState<UserPreferences>(() => loadUserPreferences(userId));

  // Reload on user change.
  useEffect(() => {
    setPrefs(loadUserPreferences(userId));
  }, [userId]);

  // Cross-tab + same-tab sync via a custom storage event.
  useEffect(() => {
    function onChange(e: Event) {
      const ce = e as CustomEvent<{ userId: string }>;
      if (ce.detail?.userId === userId) setPrefs(loadUserPreferences(userId));
    }
    window.addEventListener(STORAGE_EVENT, onChange);
    return () => window.removeEventListener(STORAGE_EVENT, onChange);
  }, [userId]);

  const update = useCallback(
    (patch: Partial<UserPreferences>) => {
      setPrefs((prev) => {
        const next: UserPreferences = {
          ...prev,
          ...patch,
          battery: { ...prev.battery, ...(patch.battery ?? {}) },
          ui: { ...prev.ui, ...(patch.ui ?? {}) },
          help: { ...prev.help, ...(patch.help ?? {}) },
          cleanedZones: { ...prev.cleanedZones, ...(patch.cleanedZones ?? {}) },
          // `tableLayouts` is deliberately NOT merged: it arrives whole
          // from `...patch`, because resetting a table's layout means
          // REMOVING its key, which a merge could never do.
        };
        saveUserPreferences(userId, next);
        // The low-battery choice's own key only when the choice itself
        // changes: written on every save, this tab's copy from before would
        // undo an untick made in another tab (`batteryStorageKey`).
        if (patch.battery) saveBatteryPreferences(userId, next.battery);
        // Told to the other subscribers AFTER this update, not inside it:
        // React may run this updater while rendering, and every other
        // `useUserPreferences` reloads (setState) on the event -- "Cannot
        // update a component while rendering a different component".
        queueMicrotask(() =>
          window.dispatchEvent(new CustomEvent(STORAGE_EVENT, { detail: { userId } })),
        );
        return next;
      });
    },
    [userId],
  );

  const reset = useCallback(() => {
    setPrefs(DEFAULT_USER_PREFERENCES);
    saveUserPreferences(userId, DEFAULT_USER_PREFERENCES);
    saveBatteryPreferences(userId, DEFAULT_USER_PREFERENCES.battery);
    window.dispatchEvent(new CustomEvent(STORAGE_EVENT, { detail: { userId } }));
  }, [userId]);

  return useMemo(() => ({ prefs, update, reset }), [prefs, update, reset]);
}